/** Local-only observations. No identifiers, payload metadata, storage or telemetry.
 * Server metric names mirror api/_lib/voice-timing.ts (rule #4 boundary).
 * Neither side imports the other's runtime. The Phase A probe checks parity.
 */
export const SERVER_VOICE_STAGES = [
  'total', 'admission', 'auth', 'claim', 'verify', 'metadata_before',
  'download', 'consume_hash', 'metadata_after', 'renew', 'stt', 'stt_body',
  'complete', 'register', 'sign_upload', 'lookup', 'mark_uploaded', 'response',
] as const;
const CLIENT_STAGES = [
  'total', 'finalize', 'lock_wait', 'capture_commit', 'upload', 'pointer_commit',
  'transcribe', 'transcript_commit', 'delivery', 'stop_to_delivery',
  'verify_copy', 'register', 'storage_upload', 'confirm_upload', 'auth',
  'request', 'response_body', 'retry_wait', 'paint_opportunity',
] as const;
type Stage = typeof CLIENT_STAGES[number];
type Scope = 'stop' | 'upload' | 'transcribe' | 'register' | 'uploaded' | 'paint';
type Sample = { stage: string; ms: number; complete: boolean };
const allowed = new Set<string>(CLIENT_STAGES);
const serverAllowed = new Set<string>(SERVER_VOICE_STAGES);
const MAX_SAMPLES = 96;
const MAX_MS = 600_000;

export class VoiceTiming {
  private starts = new Map<Stage, number>();
  private samples: Sample[] = [];
  private closed = false;
  private scope: Scope;
  private now: () => number;
  constructor(scope: Scope, now: () => number = () => performance.now()) {
    this.scope = scope;
    this.now = now;
  }
  private clock(): number | null {
    try { const value = this.now(); return Number.isFinite(value) ? value : null; } catch { return null; }
  }
  start(stage: Stage): void {
    if (this.closed || !allowed.has(stage) || this.starts.has(stage)) return;
    const value = this.clock();
    if (value !== null) this.starts.set(stage, value);
  }
  end(stage: Stage): void { this.closeStage(stage, true); }
  private closeStage(stage: Stage, complete: boolean): void {
    const start = this.starts.get(stage);
    this.starts.delete(stage);
    const end = this.clock();
    if (start === undefined || end === null || this.closed || this.samples.length >= MAX_SAMPLES) return;
    this.samples.push({ stage, ms: Math.round(Math.min(MAX_MS, Math.max(0, end - start)) * 10) / 10, complete });
  }
  server(response: Response): void {
    if (this.closed) return;
    // Treat the header as untrusted. Never retain/log arbitrary names or descriptions.
    try {
      const raw = response.headers.get('Server-Timing');
      if (!raw || raw.length > 8192) return;
      for (const field of raw.split(',')) {
        const match = /^\s*vb_([a-z_]+);dur=([0-9]+(?:\.[0-9]+)?)\s*$/.exec(field);
        if (!match || !serverAllowed.has(match[1]) || this.samples.length >= MAX_SAMPLES) continue;
        const ms = Number(match[2]);
        if (Number.isFinite(ms) && ms <= MAX_MS) this.samples.push({ stage: `server_${match[1]}`, ms, complete: true });
      }
    } catch { /* Observation cannot change the request outcome. */ }
  }
  finish(): void {
    if (this.closed) return;
    this.end('total');
    for (const stage of this.starts.keys()) this.closeStage(stage, false);
    this.closed = true;
    try { console.info('[voice-batch-timing]', JSON.stringify({ scope: this.scope, stages: this.samples })); } catch { /* Observer only. */ }
  }
}

export function createVoiceTiming(scope: Scope): VoiceTiming | undefined {
  // Optional chaining also leaves existing plain-Node custody probes flag-off.
  if (import.meta.env?.VITE_VOICE_BATCH_TIMING_ENABLED !== 'true') return undefined;
  const timing = new VoiceTiming(scope);
  timing.start('total');
  return timing;
}

/** Two frames measure an opportunity to paint, NOT proof that target text rendered.
 * Never awaited by capture custody; background/hidden samples are incomplete.
 */
export function observeVoicePaintOpportunity(): void {
  const timing = createVoiceTiming('paint');
  if (!timing) return;
  timing.start('paint_opportunity');
  try {
    if (document.visibilityState !== 'visible') { timing.finish(); return; }
    let closed = false;
    const timeout = setTimeout(() => { closed = true; timing.finish(); }, 1000);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (closed) return;
      closed = true;
      clearTimeout(timeout);
      if (document.visibilityState === 'visible') timing.end('paint_opportunity');
      timing.finish();
    }));
  } catch { timing.finish(); }
}
